import { markdownToNodes } from './markdownHelpers';
import { createSuggestionIdentity, createSuggestionMarkAttrs } from './suggestionHelpers';

const ADD_MARK = 'diffSuggestionAdd';
const REMOVE_MARK = 'diffSuggestionRemove';

const cloneJson = (value) => {
  try {
    return JSON.parse(JSON.stringify(value));
  } catch {
    return value;
  }
};

const normalizeDocJson = (docJson) => {
  const base = docJson && typeof docJson === 'object' ? cloneJson(docJson) : null;
  const content = Array.isArray(base?.content) ? base.content : [];
  return {
    ...(base || {}),
    type: 'doc',
    content
  };
};

const toNodeText = (node) => {
  if (!node || typeof node !== 'object') return '';
  if (typeof node.text === 'string') return node.text;
  const children = Array.isArray(node.content) ? node.content : [];
  return children.map(toNodeText).join('');
};

const toNodesText = (nodes = []) => {
  return (Array.isArray(nodes) ? nodes : []).map(toNodeText).join('');
};

const normalizeMatchText = (value = '') => {
  return String(value || '')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
};

const hasSuggestionMarkInNode = (node) => {
  if (!node || typeof node !== 'object') return false;
  const marks = Array.isArray(node.marks) ? node.marks : [];
  if (marks.some((mark) => {
    const type = String(mark?.type || '').trim();
    return type === ADD_MARK || type === REMOVE_MARK;
  })) {
    return true;
  }
  const children = Array.isArray(node.content) ? node.content : [];
  return children.some(hasSuggestionMarkInNode);
};

const stripSuggestionMarksFromNode = (node) => {
  if (!node || typeof node !== 'object') return node;

  const next = { ...node };
  if (Array.isArray(node.marks)) {
    next.marks = node.marks.filter((mark) => {
      const type = String(mark?.type || '').trim();
      return type !== ADD_MARK && type !== REMOVE_MARK;
    });
    if (next.marks.length === 0) delete next.marks;
  }

  if (Array.isArray(node.content)) {
    next.content = node.content.map(stripSuggestionMarksFromNode);
  }

  return next;
};

const applySuggestionMarkToNode = (node, markType, attrs) => {
  if (!node || typeof node !== 'object') return node;

  const next = { ...node };
  if (typeof next.text === 'string') {
    const marks = Array.isArray(next.marks) ? [...next.marks] : [];
    marks.push({ type: markType, attrs });
    next.marks = marks;
    return next;
  }

  if (Array.isArray(next.content)) {
    next.content = next.content.map((child) => applySuggestionMarkToNode(child, markType, attrs));
  }

  return next;
};

const findAnchorPath = (nodes = [], targetAnchorId, path = [], options = {}) => {
  const preferUnmarked = options?.preferUnmarked !== false;
  let firstMarkedHit = null;
  const list = Array.isArray(nodes) ? nodes : [];
  for (let index = 0; index < list.length; index += 1) {
    const node = list[index];
    const nodeAnchor = String(node?.attrs?.node_anchor_id || '').trim();
    const nextPath = [...path, index];
    if (nodeAnchor && nodeAnchor === targetAnchorId) {
      if (!preferUnmarked || !hasSuggestionMarkInNode(node)) {
        return nextPath;
      }
      if (!firstMarkedHit) {
        firstMarkedHit = nextPath;
      }
    }
    const childContent = Array.isArray(node?.content) ? node.content : [];
    if (childContent.length > 0) {
      const hit = findAnchorPath(childContent, targetAnchorId, [...nextPath, 'content'], options);
      if (hit) return hit;
    }
  }
  return firstMarkedHit;
};

const isPathWithin = (candidatePath = [], ancestorPath = []) => {
  if (!Array.isArray(candidatePath) || !Array.isArray(ancestorPath)) return false;
  if (ancestorPath.length === 0 || candidatePath.length < ancestorPath.length) return false;
  for (let i = 0; i < ancestorPath.length; i += 1) {
    if (candidatePath[i] !== ancestorPath[i]) return false;
  }
  return true;
};

const findHeadingLevelPath = (nodes = [], level = 1, path = [], options = {}) => {
  const preferUnmarked = options?.preferUnmarked !== false;
  let firstMarkedHit = null;
  const list = Array.isArray(nodes) ? nodes : [];
  for (let index = 0; index < list.length; index += 1) {
    const node = list[index];
    const nextPath = [...path, index];
    const nodeType = String(node?.type || '').trim().toLowerCase();
    const nodeLevel = Number(node?.attrs?.level || 0);

    if (nodeType === 'heading' && nodeLevel === level) {
      if (!preferUnmarked || !hasSuggestionMarkInNode(node)) {
        return nextPath;
      }
      if (!firstMarkedHit) {
        firstMarkedHit = nextPath;
      }
    }

    const childContent = Array.isArray(node?.content) ? node.content : [];
    if (childContent.length > 0) {
      const hit = findHeadingLevelPath(childContent, level, [...nextPath, 'content'], options);
      if (hit) return hit;
    }
  }

  return firstMarkedHit;
};

const findNodeTypeOrdinalPath = (nodes = [], typeName = '', ordinal = 1, path = [], options = {}) => {
  const preferUnmarked = options?.preferUnmarked !== false;
  const targetType = String(typeName || '').trim().toLowerCase();
  const targetOrdinal = Number(ordinal);
  if (!targetType || !Number.isFinite(targetOrdinal) || targetOrdinal < 1) return null;

  const matches = [];
  const collect = (list = [], currentPath = []) => {
    const arr = Array.isArray(list) ? list : [];
    for (let index = 0; index < arr.length; index += 1) {
      const node = arr[index];
      const nextPath = [...currentPath, index];
      const nodeType = String(node?.type || '').trim().toLowerCase();
      if (nodeType === targetType) {
        matches.push({
          path: nextPath,
          marked: hasSuggestionMarkInNode(node)
        });
      }
      const childContent = Array.isArray(node?.content) ? node.content : [];
      if (childContent.length > 0) {
        collect(childContent, [...nextPath, 'content']);
      }
    }
  };

  collect(nodes, path);

  const orderedMatches = preferUnmarked
    ? (() => {
      const unmarked = matches.filter((entry) => !entry.marked);
      return unmarked.length >= targetOrdinal ? unmarked : matches;
    })()
    : matches;

  if (orderedMatches.length < targetOrdinal) return null;
  const candidate = orderedMatches[targetOrdinal - 1];
  if (!candidate) return null;
  if (!preferUnmarked || !candidate.marked) return candidate.path;

  return candidate.path;
};

const toDescendantContentPath = (anchorPath = []) => {
  if (!Array.isArray(anchorPath) || anchorPath.length === 0) return ['content'];
  return [...anchorPath, 'content'];
};

const resolveTargetPath = ({ docJson, targetAnchorId, targetTextSelector }) => {
  const anchorPath = findAnchorPath(docJson?.content, targetAnchorId, [], { preferUnmarked: true });
  if (!anchorPath) {
    return {
      path: null,
      reason: 'anchor_not_found'
    };
  }

  const selector = String(targetTextSelector || '').trim();
  if (!selector) {
    return { path: anchorPath, reason: null };
  }

  const selectorCandidates = [
    selector,
    `${targetAnchorId}:${selector}`,
    `${targetAnchorId}_${selector}`,
    `${targetAnchorId}-${selector}`
  ];

  for (const candidate of selectorCandidates) {
    const candidatePath = findAnchorPath(docJson?.content, candidate, [], { preferUnmarked: true });
    if (candidatePath && isPathWithin(candidatePath, anchorPath)) {
      return { path: candidatePath, reason: null };
    }
  }

  const headingMatch = selector.match(/^h([1-6])$/i);
  if (headingMatch) {
    const headingLevel = Number(headingMatch[1]);
    const descendantPath = toDescendantContentPath(anchorPath);
    const headingPath = findHeadingLevelPath(
      getContentArrayByPath(docJson, ['content', ...descendantPath]),
      headingLevel,
      descendantPath,
      { preferUnmarked: true }
    );
    if (headingPath) {
      return { path: headingPath, reason: null };
    }
  }

  const paragraphMatch = selector.match(/^(?:paragraph|para|p)_([1-9]\d*)$/i);
  if (paragraphMatch) {
    const paragraphOrdinal = Number(paragraphMatch[1]);
    const descendantPath = toDescendantContentPath(anchorPath);
    const paragraphPath = findNodeTypeOrdinalPath(
      getContentArrayByPath(docJson, ['content', ...descendantPath]),
      'paragraph',
      paragraphOrdinal,
      descendantPath,
      { preferUnmarked: true }
    );
    if (paragraphPath) {
      return { path: paragraphPath, reason: null };
    }
  }

  return {
    path: anchorPath,
    reason: 'selector_not_found_fallback_anchor'
  };
};

const getContentArrayByPath = (rootDoc, pathToContent = []) => {
  let ref = rootDoc;
  for (const part of pathToContent) {
    if (ref == null) return null;
    ref = ref[part];
  }
  return Array.isArray(ref) ? ref : null;
};

const resolveArrayLocationForPath = (rootDoc, path = []) => {
  if (!Array.isArray(path) || path.length === 0) return null;
  const index = path[path.length - 1];
  const pathToContent = ['content', ...path.slice(0, -1)];

  const container = getContentArrayByPath(rootDoc, pathToContent);
  if (!container) return null;
  if (!Number.isInteger(index) || index < 0 || index >= container.length) return null;

  return { container, index };
};

const buildMarkedNodes = ({ nodes = [], op = 'add', annotationId, batchId, authorId = null, createdAt = Date.now() } = {}) => {
  const markType = op === 'remove' ? REMOVE_MARK : ADD_MARK;
  const attrs = createSuggestionMarkAttrs({
    op,
    annotationId,
    batchId,
    source: 'ai_write',
    authorId,
    createdAt,
    status: 'pending'
  });

  return (Array.isArray(nodes) ? nodes : []).map((node) => applySuggestionMarkToNode(node, markType, attrs));
};

const EXPLICIT_BLOCK_FORMAT_RE = /(^|\n)\s*(#{1,6}\s+\S|[-*+]\s+\S|\d+\.\s+\S|>\s+\S|```|~~~)|<\/?(h[1-6]|p|ul|ol|li|blockquote|pre|table|tr|td|th)\b/i;

const hasExplicitBlockFormatting = (rawContent = '') => {
  const text = String(rawContent || '').trim();
  if (!text) return false;
  return EXPLICIT_BLOCK_FORMAT_RE.test(text);
};

const toPlainObject = (value) => {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? value
    : {};
};

const pickHeadingAttrs = (attrs = {}) => {
  const source = toPlainObject(attrs);
  const picked = {};
  if (Number.isFinite(Number(source.level)) && Number(source.level) >= 1 && Number(source.level) <= 6) {
    picked.level = Number(source.level);
  }
  if (typeof source.class === 'string' && source.class.trim()) {
    picked.class = source.class;
  }
  if (typeof source.className === 'string' && source.className.trim()) {
    picked.className = source.className;
  }
  if (typeof source.textAlign === 'string' && source.textAlign.trim()) {
    picked.textAlign = source.textAlign;
  }
  return picked;
};

const preserveSourceBlockShape = ({ replacementNodes = [], sourceNodes = [], rawContent = '' } = {}) => {
  if (hasExplicitBlockFormatting(rawContent)) return replacementNodes;
  if (!Array.isArray(replacementNodes) || replacementNodes.length !== 1) return replacementNodes;
  if (!Array.isArray(sourceNodes) || sourceNodes.length !== 1) return replacementNodes;

  const sourceNode = sourceNodes[0];
  const replacementNode = replacementNodes[0];
  const sourceType = String(sourceNode?.type || '').trim().toLowerCase();
  const replacementType = String(replacementNode?.type || '').trim().toLowerCase();

  if (sourceType !== 'heading' || replacementType !== 'paragraph') {
    return replacementNodes;
  }

  const sourceHeadingAttrs = pickHeadingAttrs(sourceNode?.attrs);
  const level = Number(sourceHeadingAttrs.level || 0);
  if (!Number.isFinite(level) || level < 1 || level > 6) {
    return replacementNodes;
  }

  return [
    {
      ...replacementNode,
      type: 'heading',
      attrs: {
        ...toPlainObject(replacementNode?.attrs),
        ...sourceHeadingAttrs,
        level
      }
    }
  ];
};

const toOpNodes = (operation = {}, options = {}) => {
  const rawContent = String(operation?.new_content || '').trim();
  if (!rawContent) return [];
  const parsed = markdownToNodes(rawContent);
  const parsedNodes = Array.isArray(parsed) ? parsed : [];
  return preserveSourceBlockShape({
    replacementNodes: parsedNodes,
    sourceNodes: Array.isArray(options?.sourceNodes) ? options.sourceNodes : [],
    rawContent
  });
};

const parseParagraphSelectorOrdinal = (selector = '') => {
  const match = String(selector || '').trim().toLowerCase().match(/^(?:paragraph|para|p)_([1-9]\d*)$/);
  if (!match) return null;
  const value = Number(match[1]);
  return Number.isFinite(value) && value > 0 ? value : null;
};

const findBestSelectionRangeInContainer = (container = [], selectedText = '') => {
  const list = Array.isArray(container) ? container : [];
  const needle = normalizeMatchText(selectedText);
  if (!needle || list.length === 0) return null;

  // Prefer direct contiguous node inclusion within the selected text.
  const inclusionHits = [];
  for (let index = 0; index < list.length; index += 1) {
    const nodeText = normalizeMatchText(toNodeText(list[index]));
    if (!nodeText) continue;
    if (needle.includes(nodeText)) {
      inclusionHits.push(index);
    }
  }

  if (inclusionHits.length > 0) {
    const runs = [];
    let runStart = inclusionHits[0];
    let runEnd = inclusionHits[0];
    for (let i = 1; i < inclusionHits.length; i += 1) {
      const idx = inclusionHits[i];
      if (idx === runEnd + 1) {
        runEnd = idx;
        continue;
      }
      runs.push({ start: runStart, end: runEnd });
      runStart = idx;
      runEnd = idx;
    }
    runs.push({ start: runStart, end: runEnd });

    runs.sort((left, right) => {
      const leftSpan = left.end - left.start;
      const rightSpan = right.end - right.start;
      if (rightSpan !== leftSpan) return rightSpan - leftSpan;

      const leftChars = toNodesText(list.slice(left.start, left.end + 1)).length;
      const rightChars = toNodesText(list.slice(right.start, right.end + 1)).length;
      return rightChars - leftChars;
    });

    const bestRun = runs[0];
    if (bestRun) {
      return { start: bestRun.start, end: bestRun.end };
    }
  }

  let best = null;

  for (let start = 0; start < list.length; start += 1) {
    let joined = '';
    for (let end = start; end < list.length && end < start + 14; end += 1) {
      const part = normalizeMatchText(toNodeText(list[end]));
      joined = joined ? `${joined} ${part}`.trim() : part;
      if (!joined) continue;

      const containsNeedle = joined.includes(needle);
      if (!containsNeedle) continue;

      const spanLength = end - start + 1;
      const joinedLength = joined.length;
      const score = spanLength * 10000 + Math.abs(joinedLength - needle.length);

      if (!best || score < best.score) {
        best = { start, end, score };
      }
    }
  }

  return best ? { start: best.start, end: best.end } : null;
};

export const applyAiWriteOperationsToDocJson = ({
  docJson,
  operations = [],
  authorId = null,
  batchId = null,
  createdAt = Date.now()
} = {}) => {
  const nextDoc = normalizeDocJson(docJson);
  const ops = Array.isArray(operations) ? operations : [];
  const canonicalBatchId = String(batchId || '').trim() || createSuggestionIdentity({}).batchId;

  let addedChars = 0;
  let removedChars = 0;
  const appliedOps = [];
  const failedOps = [];

  const sortedOps = ops
    .map((operation, originalIndex) => ({ operation, originalIndex }))
    .sort((leftEntry, rightEntry) => {
      const left = leftEntry.operation;
      const right = rightEntry.operation;

      const leftAnchor = String(left?.target_anchor_id || '');
      const rightAnchor = String(right?.target_anchor_id || '');
      const anchorCompare = leftAnchor.localeCompare(rightAnchor);
      if (anchorCompare !== 0) return anchorCompare;

      const leftOrdinal = parseParagraphSelectorOrdinal(left?.target_text_selector);
      const rightOrdinal = parseParagraphSelectorOrdinal(right?.target_text_selector);
      if (Number.isFinite(leftOrdinal) && Number.isFinite(rightOrdinal) && leftOrdinal !== rightOrdinal) {
        return rightOrdinal - leftOrdinal;
      }

      return leftEntry.originalIndex - rightEntry.originalIndex;
    })
    .map((entry) => entry.operation);

  sortedOps.forEach((operation, opIndex) => {
    const opType = String(operation?.op_type || '').trim().toLowerCase();
    const targetAnchorId = String(operation?.target_anchor_id || '').trim();
    const targetTextSelector = String(operation?.target_text_selector || '').trim();
    if (!targetAnchorId) {
      failedOps.push({ index: opIndex, reason: 'missing_target_anchor_id', operation });
      return;
    }

    const targetResolution = resolveTargetPath({
      docJson: nextDoc,
      targetAnchorId,
      targetTextSelector
    });
    const path = targetResolution?.path;
    if (!path) {
      failedOps.push({ index: opIndex, reason: targetResolution?.reason || 'anchor_not_found', operation });
      return;
    }

    const location = resolveArrayLocationForPath(nextDoc, path);
    if (!location?.container) {
      failedOps.push({ index: opIndex, reason: 'anchor_resolution_failed', operation, path });
      return;
    }

    const targetNode = location.container[location.index];
    const baseTargetNode = stripSuggestionMarksFromNode(targetNode);
    const annotationId = createSuggestionIdentity({ batchId: canonicalBatchId }).annotationId;

    if (opType === 'replace_block') {
      const newNodes = toOpNodes(operation, { sourceNodes: [baseTargetNode] });
      const removedNodes = buildMarkedNodes({
        nodes: [baseTargetNode],
        op: 'remove',
        annotationId,
        batchId: canonicalBatchId,
        authorId,
        createdAt
      });
      const addedNodes = buildMarkedNodes({
        nodes: newNodes,
        op: 'add',
        annotationId,
        batchId: canonicalBatchId,
        authorId,
        createdAt
      });
      location.container.splice(location.index, 1, ...removedNodes, ...addedNodes);
      removedChars += toNodeText(baseTargetNode).length;
      addedChars += toNodesText(newNodes).length;
      appliedOps.push({ index: opIndex, opType, targetAnchorId, targetTextSelector: targetTextSelector || null, annotationId });
      return;
    }

    if (opType === 'delete_block') {
      const removedNodes = buildMarkedNodes({
        nodes: [baseTargetNode],
        op: 'remove',
        annotationId,
        batchId: canonicalBatchId,
        authorId,
        createdAt
      });
      location.container.splice(location.index, 1, ...removedNodes);
      removedChars += toNodeText(baseTargetNode).length;
      appliedOps.push({ index: opIndex, opType, targetAnchorId, targetTextSelector: targetTextSelector || null, annotationId });
      return;
    }

    if (opType === 'insert_after' || opType === 'insert_before') {
      const newNodes = toOpNodes(operation);
      const addedNodes = buildMarkedNodes({
        nodes: newNodes,
        op: 'add',
        annotationId,
        batchId: canonicalBatchId,
        authorId,
        createdAt
      });
      const insertAt = opType === 'insert_before' ? location.index : location.index + 1;
      location.container.splice(insertAt, 0, ...addedNodes);
      addedChars += toNodesText(newNodes).length;
      appliedOps.push({ index: opIndex, opType, targetAnchorId, targetTextSelector: targetTextSelector || null, annotationId });
      return;
    }

    failedOps.push({ index: opIndex, reason: 'unsupported_op_type', operation });
  });

  return {
    docJson: nextDoc,
    batchId: canonicalBatchId,
    addedChars,
    removedChars,
    appliedOps,
    failedOps,
    changed: appliedOps.length > 0
  };
};

export const applyAiWriteSelectionFallbackToDocJson = ({
  docJson,
  selectedText,
  replacementContent,
  authorId = null,
  batchId = null,
  createdAt = Date.now()
} = {}) => {
  const nextDoc = normalizeDocJson(docJson);
  const canonicalBatchId = String(batchId || '').trim() || createSuggestionIdentity({}).batchId;
  const content = Array.isArray(nextDoc.content) ? nextDoc.content : [];
  const match = findBestSelectionRangeInContainer(content, selectedText);
  if (!match) {
    return {
      docJson: nextDoc,
      batchId: canonicalBatchId,
      addedChars: 0,
      removedChars: 0,
      appliedOps: [],
      failedOps: [{ reason: 'selection_not_found' }],
      changed: false
    };
  }

  const replacementNodes = toOpNodes({ new_content: replacementContent });
  const normalizedReplacementNodes = preserveSourceBlockShape({
    replacementNodes,
    sourceNodes: content.slice(match.start, match.end + 1).map(stripSuggestionMarksFromNode),
    rawContent: replacementContent
  });
  if (!normalizedReplacementNodes.length) {
    return {
      docJson: nextDoc,
      batchId: canonicalBatchId,
      addedChars: 0,
      removedChars: 0,
      appliedOps: [],
      failedOps: [{ reason: 'missing_replacement_content' }],
      changed: false
    };
  }

  const originalNodes = content.slice(match.start, match.end + 1).map(stripSuggestionMarksFromNode);
  const annotationId = createSuggestionIdentity({ batchId: canonicalBatchId }).annotationId;
  const removedNodes = buildMarkedNodes({
    nodes: originalNodes,
    op: 'remove',
    annotationId,
    batchId: canonicalBatchId,
    authorId,
    createdAt
  });
  const addedNodes = buildMarkedNodes({
    nodes: normalizedReplacementNodes,
    op: 'add',
    annotationId,
    batchId: canonicalBatchId,
    authorId,
    createdAt
  });

  content.splice(match.start, match.end - match.start + 1, ...removedNodes, ...addedNodes);

  return {
    docJson: nextDoc,
    batchId: canonicalBatchId,
    addedChars: toNodesText(normalizedReplacementNodes).length,
    removedChars: toNodesText(originalNodes).length,
    appliedOps: [{ opType: 'replace_selection', annotationId }],
    failedOps: [],
    changed: true
  };
};
